import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { tracks } from "./tracks";
import "./MusicPlayer.css";

const formatTime = (seconds: number) => Number.isFinite(seconds)
  ? `${Math.floor(seconds / 60)}:${Math.floor(seconds % 60).toString().padStart(2, "0")}` : "0:00";

const MusicPlayer = ({ compact = false }: { compact?: boolean }) => {
  const { t } = useTranslation();
  const audioRef = useRef<HTMLAudioElement>(null);
  const [trackIndex, setTrackIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const track = tracks[trackIndex];

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.pause(); audio.load(); setPlaying(false); setCurrentTime(0);
  }, [trackIndex]);

  const toggle = async () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) { try { await audio.play(); setPlaying(true); } catch { setPlaying(false); } }
    else { audio.pause(); setPlaying(false); }
  };
  const move = (direction: number) => setTrackIndex((trackIndex + direction + tracks.length) % tracks.length);
  const subtitle = track.id === "baltiptsy" ? t("player.baltiptsySubtitle") : track.subtitle;
  const description = track.id === "baltiptsy" ? t("player.baltiptsyDescription") : track.description;

  return <section className={`music-player ${compact ? "music-player--compact" : ""}`} aria-label={t("player.region")}>
    <div className="music-player__cover-wrap">
      <img className="music-player__cover" src={track.cover} alt={t("player.coverAlt", { title: track.title })} loading="lazy" />
      <button className="music-player__main-button" type="button" onClick={toggle} aria-label={playing ? t("player.pause") : t("player.play")}>{playing ? "❚❚" : "▶"}</button>
    </div>
    <div className="music-player__body">
      <p className="eyebrow">{t("player.label")}</p><h3>{track.title}</h3>
      <p className="music-player__subtitle">{subtitle}</p>
      {!compact && <p className="music-player__description">{description}</p>}
      <audio ref={audioRef} preload="metadata" onTimeUpdate={event => setCurrentTime(event.currentTarget.currentTime)} onLoadedMetadata={event => setDuration(event.currentTarget.duration)} onEnded={() => setPlaying(false)}><source src={track.src} type="audio/mpeg" /></audio>
      <div className="music-player__timeline">
        <span>{formatTime(currentTime)}</span>
        <input aria-label={t("player.position")} type="range" min="0" max={duration || 0} value={currentTime} step="0.1" onChange={event => { const value = Number(event.target.value); if (audioRef.current) audioRef.current.currentTime = value; setCurrentTime(value); }} />
        <span>{formatTime(duration)}</span>
      </div>
      {tracks.length > 1 && <div className="music-player__skip"><button onClick={() => move(-1)} aria-label={t("player.previous")}>←</button><button onClick={() => move(1)} aria-label={t("player.next")}>→</button></div>}
    </div>
  </section>;
};
export default MusicPlayer;
